// ---- a ship as a pipeline: tagged → picked up → applied → restarting → live ------
//
// After Ship, DevOS shows where the release is instead of offering Ship again:
// the factory's tag, Argo CD picking the new tag up (its image updater looks for
// prod-* tags every few minutes), Argo applying it, prod's pod restarting on the
// image, and prod answering with the version. Pure: the stages are read from
// GET /api/builds (releases, versions, and the cluster's rollout when there is one).

import type { Builds, Rollout } from '@/db/api'

export type StageKey = 'tagged' | 'picked' | 'applied' | 'restarting' | 'live'
export type StageState = 'done' | 'active' | 'waiting' | 'failed'

export interface Stage {
  key: StageKey
  label: string
  state: StageState
  detail?: string
}

export interface Pipeline {
  sha: string
  tag: string
  /** when it was tagged for prod */
  at: string
  live: boolean
  stages: Stage[]
}

/** Whether an environment's version is this build (either may be the longer). */
export const runsVersion = (env: string, sha: string) =>
  !!env && !!sha && (env.startsWith(sha) || sha.startsWith(env))

const tagOf = (image: string) => {
  const i = image.lastIndexOf(':')
  return i >= 0 && !image.slice(i).includes('/') ? image.slice(i + 1) : ''
}

/** How long a finished ship stays on screen. */
export const SHOWN_AFTER_LIVE = 15 * 60_000

const BAD_WAITS = new Set([
  'ImagePullBackOff',
  'ErrImagePull',
  'CrashLoopBackOff',
  'CreateContainerConfigError',
  'InvalidImageName',
])

const waitWords = (reason: string) =>
  reason === 'ContainerCreating'
    ? 'pulling the image and starting'
    : reason === 'PodInitializing'
      ? 'starting'
      : reason.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()

/**
 * The newest release as a pipeline, or null when there is nothing to show (no
 * release, or one that went live a while ago). `unreachable` is a failed poll
 * while it ships: prod restarting takes this page's own server away for a moment.
 */
export const pipelineOf = (b: Builds, now: number, unreachable = false): Pipeline | null => {
  const rel = b.releases[0]
  if (!rel) return null
  const live = runsVersion(b.versions.prod ?? '', rel.sha)
  if (live && now - Date.parse(rel.at) > SHOWN_AFTER_LIVE) return null

  const ro: Rollout | undefined = b.rollout
  const applied = !!ro?.images.some((i) => tagOf(i) === rel.tag)
  const syncing = ro?.operation === 'Running' && (ro.operation_started ?? '') >= rel.at
  const opFailed = ro?.operation === 'Failed' && (ro.operation_started ?? '') >= rel.at
  const pod = ro?.pods.find((p) => tagOf(p.image) === rel.tag && !p.terminating)
  const leaving = ro?.pods.some((p) => tagOf(p.image) !== rel.tag && p.terminating)
  const badWait = pod?.waiting && BAD_WAITS.has(pod.waiting) ? pod.waiting : ''

  const picked = live || applied || syncing || !!pod
  const appliedDone = live || applied || !!pod
  const restarted = live || !!pod?.ready

  const state = (done: boolean, active: boolean, failed = false): StageState =>
    done ? 'done' : failed ? 'failed' : active ? 'active' : 'waiting'

  const stages: Stage[] = [{ key: 'tagged', label: 'Tagged for prod', state: 'done', detail: rel.tag }]
  if (ro) {
    stages.push(
      {
        key: 'picked',
        label: 'Picked up by Argo CD',
        state: state(picked, !picked),
        detail: picked ? undefined : 'its image updater looks for new prod tags every few minutes',
      },
      {
        key: 'applied',
        label: 'Applied',
        state: state(appliedDone, picked && !appliedDone, opFailed),
        detail: opFailed
          ? 'Argo CD could not apply it'
          : syncing && !appliedDone
            ? 'Argo CD is syncing'
            : undefined,
      },
      {
        key: 'restarting',
        label: 'Prod restarts on it',
        state: state(restarted, appliedDone || !!pod || !!leaving || unreachable, !!badWait),
        detail: badWait
          ? `the new pod is stuck: ${waitWords(badWait)}`
          : restarted
            ? undefined
            : unreachable
              ? 'prod is offline for a moment while it restarts'
              : pod?.waiting
                ? waitWords(pod.waiting)
                : pod
                  ? 'waiting for it to answer'
                  : leaving
                    ? 'the old version is stopping'
                    : undefined,
      },
    )
  } else {
    stages.push({
      key: 'picked',
      label: 'Prod picks it up',
      state: state(live, !live),
      detail: live ? undefined : unreachable ? 'prod is restarting' : 'usually a few minutes',
    })
  }
  stages.push({ key: 'live', label: 'Live on prod', state: live ? 'done' : 'waiting' })
  // the first stage not done is the one in hand
  if (!live) {
    const first = stages.find((s) => s.state !== 'done')
    if (first && first.state === 'waiting') first.state = 'active'
  }
  return { sha: rel.sha, tag: rel.tag, at: rel.at, live, stages }
}

/** Whether a release is on its way (tagged, prod not running it yet). */
export const shipping = (b: Builds | null | undefined) => {
  const rel = b?.releases[0]
  return !!rel && !runsVersion(b?.versions.prod ?? '', rel.sha)
}
